import * as React from 'react';
import { Formik, FormikProps, FormikHelpers } from 'formik';
import { ButtonWidget, ButtonType } from '@tableau/widgets';
import { Messages } from '../../codegen/Localize';
import { UserSettings } from '../../ts/resources/UserSettings';
import { ServiceLocator } from '../../app/ServiceLocator';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { UpdateUserDisplayNameNotification, UpdateUserEmailNotification } from '../../ts/services/UserSettingsNotification';
import { ISiteUserInfo } from '../../ts/models/ISiteUserInfo';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { appStore } from '../../app/AppStore';
import { Location } from '../../common/react/Location';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { DefaultAuthenticationProviderId } from '../../ts/services/SiteAuthenticationSettingsService';
import { EmailValidationHelper } from '../../ts/helpers/EmailValidationHelper';
import { textFieldWidget, buttonCell } from '../styles/EmailAndDisplayNameFormStyles';
import { FormikTextBox } from '../../commonInfra/components/FormikField';
import { GeneralAuthSettingsProps } from './GeneralAuthSettings';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';

export namespace EmailAndDisplayNameForm {
  const MAX_CHARACTER_LIMIT = 100;

  function getIsSAMLUser(siteUser: ISiteUserInfo): boolean {
    return !!siteUser && !!siteUser.authenticationProviderId && siteUser.authenticationProviderId !== DefaultAuthenticationProviderId;
  }
  export interface FormFields {
    displayName: string;
    email: string;
  }

  export type Props = Omit<GeneralAuthSettingsProps, 'emailFormInitialValues'> & {
    initialValues: FormFields;
    handleSubmit: (values: FormFields, formik: FormikHelpers<FormFields>, props: Pick<Props, 'initialValues'| 'siteUser' | 'serverUser'>) => void;
  }

  export function validateDisplayName(value: string) {
    if (!value || value.length > MAX_CHARACTER_LIMIT) {
      return Messages.userSettings_invalidDisplayNameMessage();
    }
  }

  export function validateEmail(value: string, isLocalAuth: boolean) {
    if(isLocalAuth && !value) return;
    if (!value || value.length > MAX_CHARACTER_LIMIT || !EmailValidationHelper.isValidEmail(value)) {
      return Messages.userSettings_invalidEmailMessage();
    }
  }

  export async function handleSubmit(values: FormFields, formik: FormikHelpers<FormFields>, props: Pick<Props, 'initialValues'| 'siteUser' | 'serverUser'>) {
    try {
      const serverUser: IServerUserInfo = props.serverUser;
      const { siteUser } = props;
      const isSAMLUser = getIsSAMLUser(siteUser);

      // display name
      const userSettings: UserSettings = ServiceLocator.get<UserSettings>('UserSettings');
      const updatePromises = [];

      if (props.initialValues.displayName !== values.displayName) {
        // update displayname promise
        const updateDisplayNamePromise = userSettings.updateUserDisplayName(serverUser.id, values.displayName)
          .then((result: IResult) => {
            // toasts
            const updateUserDisplayNameNotification: UpdateUserDisplayNameNotification = ServiceLocator.get<UpdateUserDisplayNameNotification>('UpdateUserDisplayNameNotification');
            updateUserDisplayNameNotification.notify(result, serverUser.username, values.displayName);
            return values.displayName;
          });
        updatePromises.push(updateDisplayNamePromise);
      }
      if (props.initialValues.email !== values.email) {
        const updateEmailFunction = isSAMLUser ? userSettings.updateUserEmailAndDelay.bind(userSettings) : userSettings.updateUserEmail.bind(userSettings);
        const updateEmailPromise = updateEmailFunction(serverUser.id, values.email)
          .then((result: IResult) => {
            // toasts
            const updateUserEmailNotification: UpdateUserEmailNotification = ServiceLocator.get<UpdateUserEmailNotification>('UpdateUserEmailNotification');
            updateUserEmailNotification.notify(result, serverUser.username, values.email);
            return values.email;
          });
        updatePromises.push(updateEmailPromise);
      }

      const results = await Promise.all(updatePromises);
      const userNameIsSameAsEmail = getIsSAMLUser(siteUser);
      if (userNameIsSameAsEmail) {
        // If username is tied to email address, then navigate to new user url
        const urls = siteUrlsSelector(appStore.getState());
        Location.instance.setUrl(urls.userSettingsUrl(serverUser.username, serverUser.domainName));
      } else {
        appStore.dispatch(incrementRouterSpaceReactKey());
      }
    } catch (e) {
      ToasterService.instance.pop({ type:  ToastType.Error, text: Messages.errorUnknown()});
    } finally {
      formik.resetForm();
    }
  }

  export function Form({initialValues: { displayName = '', email = ''}, ...otherProps}: Props) {
    const props: Props = {initialValues: {displayName, email}, ...otherProps};
    const siteUser: ISiteUserInfo = props.siteUser;
    const isSAMLUser = getIsSAMLUser(siteUser);
    return (
      <Formik
        initialValues={{ ...props.initialValues }}
        onSubmit={function(values: FormFields, formik: FormikHelpers<FormFields>) {
          const {siteUser, serverUser, initialValues} = props;
          props.handleSubmit(values, formik, {siteUser, serverUser, initialValues});
        }}
      >
        {(formik: FormikProps<FormFields>) => (
          <form onSubmit={formik.handleSubmit}>
            <table>
              <tbody>
                {/* Display Name Section */}
                {(props.isDomainFamilyAD || props.isViewerServerAdmin || props.isViewerSelf) &&
                  <tr data-test-id="displayname-section">
                    <td className="tb-label">
                      {Messages.displayName_title_case()}
                    </td>
                    <td className="tb-details-value">
                      {!props.updateDisplayNameAllowed && props.initialValues.displayName}
                      {props.updateDisplayNameAllowed &&
                        <FormikTextBox
                          name="displayName"
                          testId="displayName"
                          label=""
                          validate={validateDisplayName}
                          className={textFieldWidget}
                          showValidationMessage={false}
                        />
                      }
                    </td>
                  </tr>
                }
                {/* Email Section */}
                {(props.isDomainFamilyLocal || props.isDomainFamilyAD || isSAMLUser && props.isViewerServerAdmin) &&
                  <tr>
                    <td className="tb-label">{Messages.email()}</td>
                    <td className="tb-details-value">
                      <FormikTextBox
                        name="email"
                        testId="email"
                        label=""
                        validate={
                          React.useCallback(
                            (value: string) => validateEmail(value, props.isDomainFamilyLocal),
                            [validateEmail, props.isDomainFamilyLocal]
                            )
                          }
                        className={textFieldWidget}
                        showValidationMessage={false}
                      />
                    </td>
                    <td className={`tb-details-button-cell ${buttonCell}`}>
                      <ButtonWidget
                        type="submit"
                        buttonType={ButtonType.Outline}
                        testId='email-displayname-submit'
                        disabled={!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.email.trim() === props.initialValues.email.trim() && formik.values.displayName.trim() === props.initialValues.displayName.trim())}>
                        {Messages.saveChanges()}
                      </ButtonWidget>
                    </td>
                  </tr>
                }
                {props.isDomainFamilyExternal && !(isSAMLUser && props.isViewerServerAdmin) &&
                  <tr data-test-id="email-value-section">
                    <td className="tb-label">{Messages.email()}</td>
                    <td className="tb-details-value" tb-test-id="email">{props.initialValues.email}</td>
                  </tr>
                }
              </tbody>
            </table>
          </form>
        )}
      </Formik>
    );
  }
}